import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Clock3,
  DollarSign,
  Timer,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Bell,
  AlertTriangle,
  ChartNoAxesCombined,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { KpiCard } from '@/components/shared/KpiCard';
import { PageHeader } from '@/components/shared/PageHeader';
import { RefreshingIndicator } from '@/components/shared/RefreshingIndicator';
import { SlideOverPanel } from '@/components/shared/SlideOverPanel';
import { StateCard } from '@/components/shared/StateCard';
import {
  SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
  SmsConsentConfirmation,
} from '@/components/shared/SmsConsentConfirmation';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { fetchApprovalById, sendApprovalReminder, type ApprovalDetail } from '@/lib/approvals';
import { type PendingAgingMode, type ReportsDateRange } from '@/lib/reports';
import { formatCurrency } from '@/lib/tickets';
import { useReportsOverviewQuery } from '@/hooks/use-app-queries';

const chartTooltipStyle = {
  background: 'hsl(0, 0%, 100%)',
  border: '1px solid hsl(215, 20%, 88%)',
  borderRadius: '8px',
  fontSize: '12px',
};

const PIE_COLORS = ['hsl(214, 90%, 45%)', 'hsl(32, 90%, 52%)', 'hsl(152, 60%, 36%)'];

function formatAvgApprovalTime(days: number) {
  const totalMinutes = Math.max(0, Math.round((Number(days) || 0) * 24 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

function formatHoursPerDay(hours: number) {
  return `${Number(hours || 0).toFixed(1)}h`;
}

function formatProjectsPerDay(projects: number) {
  return Number(projects || 0).toFixed(1);
}

function ChartSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-5 w-48" />
      <Skeleton className="h-56 w-full" />
    </div>
  );
}

function RingPercentCard({
  label,
  value,
  color = 'hsl(152, 60%, 36%)',
  labelColor,
}: {
  label: string;
  value: number;
  color?: string;
  labelColor?: string;
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  const data = [
    { name: 'value', value: clamped },
    { name: 'remaining', value: 100 - clamped },
  ];

  return (
    <div className="p-3">
      <div className="relative mx-auto h-36 w-36">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={[{ name: 'background', value: 100 }]}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={43}
              outerRadius={50}
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive={false}
            >
              <Cell fill="hsl(215, 22%, 90%)" />
            </Pie>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={43}
              outerRadius={50}
              startAngle={90}
              endAngle={-270}
              stroke="none"
            >
              <Cell fill={color} />
              <Cell fill="transparent" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold" style={{ color }}>
            {clamped}%
          </span>
        </div>
      </div>
      <p
        className="-mt-3 text-center text-sm font-medium"
        style={{ color: labelColor ?? 'hsl(215, 14%, 46%)' }}
      >
        {label}
      </p>
    </div>
  );
}

function eventLabel(t: (key: string, options?: Record<string, unknown>) => string, type: string) {
  switch (type) {
    case 'TICKET_CREATED':
      return t('reports.events.ticketCreated');
    case 'SENT_FOR_APPROVAL':
      return t('reports.events.sentForApproval');
    case 'APPROVAL_EMAIL_SENT':
      return t('reports.events.approvalEmailSent');
    case 'APPROVAL_SMS_SENT':
      return t('reports.events.approvalSmsSent');
    case 'APPROVAL_LINK_OPENED':
      return t('reports.events.opened');
    case 'APPROVAL_LINK_SMS_RESENT':
      return t('reports.events.approvalLinkSmsResent');
    case 'REMINDER_SENT':
      return t('reports.events.reminderSent');
    case 'REMINDER_SMS_SENT':
      return t('reports.events.reminderSmsSent');
    case 'SMS_STATUS_QUEUED':
      return t('reports.events.smsQueued');
    case 'SMS_STATUS_SENT':
      return t('reports.events.smsSent');
    case 'SMS_STATUS_DELIVERED':
      return t('reports.events.smsDelivered');
    case 'SMS_STATUS_FAILED':
      return t('reports.events.smsFailed');
    case 'SMS_STATUS_UNDELIVERED':
      return t('reports.events.smsUndelivered');
    case 'APPROVED':
      return t('reports.events.approved');
    case 'REJECTED':
      return t('reports.events.rejected');
    case 'COMMENT_ADDED':
      return t('reports.events.commentAdded');
    case 'DELETED':
      return t('reports.events.deleted');
    default:
      return type.replaceAll('_', ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  }
}

export default function Reports() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [dateRange, setDateRange] = useState<ReportsDateRange>('last_30_days');
  const [pendingAgingMode, setPendingAgingMode] = useState<PendingAgingMode>('count');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (dateRange !== 'custom') return;

    const today = new Date();
    const todayValue = today.toISOString().slice(0, 10);
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const startValue = thirtyDaysAgo.toISOString().slice(0, 10);

    if (!startDate) {
      setStartDate(startValue);
    }

    if (!endDate) {
      setEndDate(todayValue);
    }
  }, [dateRange, startDate, endDate]);

  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(null);
  const [approvalDetail, setApprovalDetail] = useState<ApprovalDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [remindingId, setRemindingId] = useState<string | null>(null);
  const [smsConsentConfirmed, setSmsConsentConfirmed] = useState(false);
  const [smsConsentError, setSmsConsentError] = useState('');
  const overviewQuery = useReportsOverviewQuery({
    dateRange,
    pendingAgingMode,
    startDate: dateRange === 'custom' ? startDate || undefined : undefined,
    endDate: dateRange === 'custom' ? endDate || undefined : undefined,
  });
  const overview = overviewQuery.data ?? null;
  const loading = overviewQuery.isLoading && !overviewQuery.data;
  const error = overviewQuery.error instanceof Error ? overviewQuery.error.message : null;
  const isRefreshing = overviewQuery.isFetching && Boolean(overviewQuery.data);
  const dateTimeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.resolvedLanguage?.startsWith('es') ? 'es-MX' : 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.resolvedLanguage],
  );

  const openApproval = async (approvalRequestId: string) => {
    setSelectedApprovalId(approvalRequestId);
    setDetailLoading(true);
    try {
      const detail = await fetchApprovalById(approvalRequestId);
      setApprovalDetail(detail);
      setSmsConsentConfirmed(false);
      setSmsConsentError('');
    } catch (err) {
      toast({
        title: t('reports.approvalDetailFailed'),
        description: err instanceof Error ? err.message : t('common.requestFailed'),
        variant: 'destructive',
      });
      setSelectedApprovalId(null);
      setApprovalDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const remind = async (approvalRequestId: string) => {
    const usesSms =
      approvalDetail?.id === approvalRequestId &&
      (approvalDetail.deliveryMethod === 'SMS' ||
        approvalDetail.deliveryMethod === 'EMAIL_AND_SMS');
    if (usesSms && !smsConsentConfirmed) {
      setSmsConsentError(SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE);
      toast({
        title: SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
        variant: 'destructive',
      });
      return;
    }
    setRemindingId(approvalRequestId);
    try {
      await sendApprovalReminder(
        approvalRequestId,
        usesSms ? { smsConsentConfirmed } : undefined,
      );
      toast({ title: t('reports.reminderSent') });
      await queryClient.invalidateQueries({ queryKey: ['reports-overview'] });
      if (selectedApprovalId === approvalRequestId) {
        const detail = await fetchApprovalById(approvalRequestId);
        setApprovalDetail(detail);
      }
    } catch (err) {
      toast({
        title: t('reports.reminderFailed'),
        description: err instanceof Error ? err.message : t('common.tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setRemindingId(null);
      if (usesSms) {
        setSmsConsentConfirmed(false);
        setSmsConsentError('');
      }
    }
  };

  const ticketTypeDistribution = useMemo(
    () => overview?.charts.ticketTypeDistribution ?? [],
    [overview],
  );
  const ticketTypeTotal = useMemo(
    () => ticketTypeDistribution.reduce((sum, item) => sum + item.count, 0),
    [ticketTypeDistribution],
  );
  const collectedPaymentsTrend = useMemo(
    () => overview?.charts.collectedPaymentsTrend ?? [],
    [overview],
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title={t("pages.reports.title")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[96px]">
              <RefreshingIndicator visible={isRefreshing} />
            </div>
            {dateRange === 'custom' ? (
              <>
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  className="h-9 rounded-md border bg-card px-3 text-sm text-foreground"
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  className="h-9 rounded-md border bg-card px-3 text-sm text-foreground"
                />
              </>
            ) : null}
            <select
              className="h-9 rounded-md border bg-card px-3 text-sm text-foreground"
              value={dateRange}
              onChange={(event) => setDateRange(event.target.value as ReportsDateRange)}
            >
              <option value="this_month">{t('reports.ranges.thisMonth')}</option>
              <option value="last_30_days">{t('reports.ranges.last30Days')}</option>
              <option value="this_quarter">{t('reports.ranges.thisQuarter')}</option>
              <option value="custom">{t('reports.ranges.custom')}</option>
            </select>
          </div>
        }
      />

      {error ? (
        <StateCard
          icon={AlertTriangle}
          tone="error"
          title={t('reports.failedToLoad')}
          description={error}
          primaryActionLabel={t('common.retry')}
          onPrimaryAction={() => void overviewQuery.refetch()}
        />
      ) : loading ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
          <TableSkeleton rows={4} columns={2} />
        </>
      ) : !overview || !overview.hasData ? (
        <StateCard
          icon={ChartNoAxesCombined}
          title={t('reports.noDataForFilters')}
          description={t('reports.tryAnotherRange')}
          primaryActionLabel={t('reports.resetFilters')}
          onPrimaryAction={() => {
            setDateRange('last_30_days');
            setPendingAgingMode('count');
            setStartDate('');
            setEndDate('');
          }}
        />
      ) : (
        <>
          <div className="rounded-lg">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <RingPercentCard
                label={t('reports.approvalRate')}
                value={overview.kpis.approvalRate * 100}
                color="hsl(152, 60%, 36%)"
                labelColor="hsl(152, 60%, 36%)"
              />
              <RingPercentCard
                label={t('reports.pendingRate')}
                value={overview.kpis.pendingRate * 100}
                color="hsl(38, 92%, 50%)"
                labelColor="hsl(38, 92%, 50%)"
              />
              <RingPercentCard
                label={t('reports.rejectionRate')}
                value={overview.kpis.rejectionRate * 100}
                color="hsl(0, 72%, 51%)"
                labelColor="hsl(0, 72%, 51%)"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              title={t('reports.activeProjects')}
              value={overview.kpis.projectStatusCounts.active}
              icon={TrendingUp}
              tone="success"
            />
            <KpiCard
              title={t('reports.onHoldProjects')}
              value={overview.kpis.projectStatusCounts.onHold}
              icon={Timer}
              tone="warning"
            />
            <KpiCard
              title={t('reports.completedProjects')}
              value={overview.kpis.projectStatusCounts.completed}
              icon={CheckCircle2}
              tone="info"
            />
            <KpiCard
              title={t('reports.cancelledProjects')}
              value={overview.kpis.projectStatusCounts.cancelled}
              icon={XCircle}
              tone="danger"
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="rounded-lg border bg-card p-5 space-y-3">
                <h3 className="font-semibold text-primary">{t('reports.ticketTypeDistribution')}</h3>
                {ticketTypeTotal === 0 ? (
                  <div className="h-56 flex items-center justify-center rounded-md border border-dashed bg-muted/20 px-4 text-center">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-foreground">{t('reports.noTicketsInRange')}</p>
                      <p className="text-xs text-muted-foreground">{t('reports.selectAnotherRangeDistribution')}</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_210px] gap-4 items-center">
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={ticketTypeDistribution}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={82}
                            paddingAngle={3}
                            dataKey="count"
                            nameKey="label"
                          >
                            {ticketTypeDistribution.map((entry, idx) => (
                              <Cell key={entry.key} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            contentStyle={chartTooltipStyle}
                            formatter={(value: number, _name, props) => [value, props.payload?.label ?? t('nav.tickets')]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2">
                      {ticketTypeDistribution.map((item, idx) => (
                        <div key={item.key} className="flex items-center justify-between gap-2 text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                            <span className="text-muted-foreground truncate">
                              {item.key === 'TM_TICKET'
                                ? t('ticketTypes.estimate')
                                : item.key === 'CHANGE_ORDER'
                                  ? t('ticketTypes.changeOrder')
                                  : item.key === 'EXTRA_WORK'
                                    ? t('ticketTypes.extraWork')
                                    : item.label}
                            </span>
                          </div>
                          <span className="font-semibold text-foreground shrink-0">
                            {item.count} ({ticketTypeTotal ? Math.round((item.count / ticketTypeTotal) * 100) : 0}%)
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-lg border bg-card p-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-md border border-sky-500/20 bg-sky-500/10 px-4 py-4 h-full">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-3">
                        <span className="text-sm text-sky-700">{t('dashboard.avgApprovalTime')}</span>
                        <div className="text-2xl font-semibold text-sky-500">
                          {formatAvgApprovalTime(overview.kpis.avgApprovalTimeDays)}
                        </div>
                      </div>
                      <Clock3 className="h-6 w-6 shrink-0 text-sky-600" />
                    </div>
                  </div>

                  <div className="rounded-md border border-success/20 bg-success/10 px-4 py-4 h-full">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-2">
                        <span className="text-sm text-success">{t('dashboard.pendingValue')}</span>
                        <div className="text-2xl font-semibold text-emerald-500">
                          {formatCurrency(overview.kpis.pendingValue)}
                        </div>
                        <p className="text-xs leading-5 text-muted-foreground">
                          {t('reports.pendingValueDescription')}
                        </p>
                      </div>
                      <DollarSign className="h-6 w-6 shrink-0 text-success" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-lg border bg-card p-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-md border border-sky-500/20 bg-sky-500/10 px-4 py-4 h-full">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-2">
                        <span className="text-sm text-sky-700">{t('reports.avgWorkHoursPerDay')}</span>
                        <div className="text-2xl font-semibold text-sky-500">
                          {formatHoursPerDay(overview.kpis.avgWorkHoursPerDay)}
                        </div>
                        <p className="text-xs leading-5 text-muted-foreground">
                          {t('reports.avgWorkHoursPerDayDescription')}
                        </p>
                      </div>
                      <Clock3 className="h-6 w-6 shrink-0 text-sky-600" />
                    </div>
                  </div>

                  <div className="rounded-md border border-violet-500/20 bg-violet-500/10 px-4 py-4 h-full">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-2">
                        <span className="text-sm text-violet-700">{t('reports.avgProjectsPerDay')}</span>
                        <div className="text-2xl font-semibold text-violet-600">
                          {formatProjectsPerDay(overview.kpis.avgProjectsPerDay)}
                        </div>
                        <p className="text-xs leading-5 text-muted-foreground">
                          {t('reports.avgProjectsPerDayDescription')}
                        </p>
                      </div>
                      <Building2 className="h-6 w-6 shrink-0 text-violet-600" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border bg-card p-5 space-y-3">
                <h3 className="font-semibold text-primary">{t('reports.pipelineValue')}</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { key: 'pending', label: t('ticketStatus.PENDING_APPROVAL'), value: overview.kpis.pendingValue, fill: 'hsl(32, 90%, 52%)' },
                        { key: 'clarification', label: t('ticketStatus.NEEDS_CLARIFICATION'), value: overview.kpis.needsClarificationValue, fill: 'hsl(214, 90%, 45%)' },
                        { key: 'approved', label: t('ticketStatus.APPROVED'), value: overview.kpis.approvedValue, fill: 'hsl(152, 60%, 36%)' },
                      ]}
                      margin={{ left: 10, right: 10, top: 10, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(215, 20%, 88%)" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12, fill: 'hsl(215, 14%, 46%)' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: 'hsl(215, 14%, 46%)' }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => `$${(Number(v) / 1000).toFixed(0)}K`}
                      />
                      <RechartsTooltip
                        contentStyle={chartTooltipStyle}
                        formatter={(value: number, _name: string, props) => [
                          formatCurrency(value),
                          props?.payload?.label ?? t('tickets.value'),
                        ]}
                      />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={56}>
                        {[
                          { key: 'pending', fill: 'hsl(32, 90%, 52%)' },
                          { key: 'clarification', fill: 'hsl(214, 90%, 45%)' },
                          { key: 'approved', fill: 'hsl(152, 60%, 36%)' },
                        ].map((entry) => (
                          <Cell key={entry.key} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-card p-5 space-y-3">
            <h3 className="font-semibold text-primary">{t('reports.collectedPaymentsTrend')}</h3>
            {collectedPaymentsTrend.length === 0 ? (
              <div className="h-72 flex items-center justify-center rounded-md border border-dashed bg-muted/20 px-4 text-center">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">{t('reports.noPaymentRecordsInRange')}</p>
                  <p className="text-xs text-muted-foreground">{t('reports.selectAnotherRangePayments')}</p>
                </div>
              </div>
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={collectedPaymentsTrend} margin={{ left: 12, right: 18, top: 22, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(215, 20%, 88%)" />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: 'hsl(215, 14%, 46%)' }} axisLine={false} tickLine={false} />
                    <YAxis
                      width={86}
                      tick={{ fontSize: 12, fill: 'hsl(215, 14%, 46%)' }}
                      axisLine={false}
                      tickLine={false}
                      domain={[0, 'dataMax + 100']}
                      tickFormatter={(v) => formatCurrency(Number(v))}
                    />
                    <RechartsTooltip
                      contentStyle={chartTooltipStyle}
                      formatter={(value: number, name: string) => {
                        const labelMap: Record<string, string> = {
                          total: t('reports.totalCollected'),
                          cash: t('paymentMethods.cash'),
                          check: t('paymentMethods.check'),
                          card: t('paymentMethods.card'),
                          zelle: t('paymentMethods.zelle'),
                          ach: t('paymentMethods.ach'),
                          other: t('paymentMethods.other'),
                        };
                        return [formatCurrency(value), labelMap[name] ?? name];
                      }}
                    />

                    <Line type="monotone" dataKey="total" name="total" stroke="hsl(220, 55%, 18%)" strokeWidth={3} dot={{ r: 2.5 }} />
                    <Line type="monotone" dataKey="cash" name="cash" stroke="hsl(152, 60%, 36%)" strokeWidth={1.6} strokeOpacity={0.5} dot={false} />
                    <Line type="monotone" dataKey="check" name="check" stroke="hsl(214, 90%, 45%)" strokeWidth={1.6} strokeOpacity={0.5} dot={false} />
                    <Line type="monotone" dataKey="card" name="card" stroke="hsl(38, 92%, 50%)" strokeWidth={1.6} strokeOpacity={0.5} dot={false} />
                    <Line type="monotone" dataKey="zelle" name="zelle" stroke="hsl(270, 55%, 55%)" strokeWidth={1.6} strokeOpacity={0.5} dot={false} />
                    <Line type="monotone" dataKey="ach" name="ach" stroke="hsl(194, 70%, 42%)" strokeWidth={1.6} strokeOpacity={0.5} dot={false} />
                    <Line type="monotone" dataKey="other" name="other" stroke="hsl(0, 72%, 51%)" strokeWidth={1.6} strokeOpacity={0.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}

      <SlideOverPanel
        open={Boolean(selectedApprovalId)}
        onClose={() => {
          setSelectedApprovalId(null);
          setApprovalDetail(null);
        }}
        title={
          approvalDetail
            ? t('reports.approvalTitle', { ticketNumber: approvalDetail.ticketNumber })
            : t('reports.approvalDetail')
        }
      >
        {detailLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : approvalDetail ? (
          <div className="space-y-4">
            <div className="rounded-md border p-3 space-y-1">
              <p className="text-sm font-semibold">{approvalDetail.title}</p>
              <p className="text-xs text-muted-foreground">
                {t('reports.recipient')}: {approvalDetail.recipientName} ({approvalDetail.recipientEmail})
              </p>
              <p className="text-xs text-muted-foreground">
                {t('reports.sent')}: {approvalDetail.sentAt ? dateTimeFormatter.format(new Date(approvalDetail.sentAt)) : '—'}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('reports.lastReminder')}: {approvalDetail.lastReminderAt ? dateTimeFormatter.format(new Date(approvalDetail.lastReminderAt)) : '—'}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold">{t('reports.timeline')}</p>
              {approvalDetail.events.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('reports.noTimelineEvents')}</p>
              ) : (
                <div className="space-y-2">
                  {approvalDetail.events.map((event) => (
                    <div key={event.id} className="rounded-md border px-3 py-2">
                      <p className="text-sm font-medium">{eventLabel(t, event.eventType)}</p>
                      <p className="text-xs text-muted-foreground">
                        {event.actorName ?? event.actorEmail ?? t('reports.system')} · {dateTimeFormatter.format(new Date(event.happenedAt))}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="pt-2">
              {approvalDetail.deliveryMethod === 'SMS' ||
              approvalDetail.deliveryMethod === 'EMAIL_AND_SMS' ? (
                <SmsConsentConfirmation
                  checked={smsConsentConfirmed}
                  onCheckedChange={(checked) => {
                    setSmsConsentConfirmed(checked);
                    if (checked) setSmsConsentError('');
                  }}
                  error={smsConsentError}
                  className="mb-2"
                />
              ) : null}
              <Button
                className="w-full"
                onClick={() => selectedApprovalId && void remind(selectedApprovalId)}
                disabled={!selectedApprovalId || remindingId === selectedApprovalId}
              >
                <Bell className="h-4 w-4 mr-2" />
                {remindingId === selectedApprovalId ? t('reports.sendingReminder') : t('reports.sendReminder')}
              </Button>
              <Button
                variant="outline"
                className="w-full mt-2"
                onClick={() => navigate(`/tickets/${approvalDetail.ticketId}`)}
              >
                {t('reports.openTicket')}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('reports.approvalDetailUnavailable')}</p>
        )}
      </SlideOverPanel>
    </div>
  );
}
